// Tiny pixel-silhouette rendering for the low-res virtual backbuffer (see
// VIRTUAL_WIDTH/VIRTUAL_HEIGHT in config/constants.js). A "sprite" is just a
// grid of characters (one row per pixel row, one char per pixel column) plus
// a palette mapping each non-space character to a color, drawn with plain
// fillRect calls — no image assets, so hue swaps (phase tint, dash flash)
// are just a palette substitution rather than a separate asset per state.

/** @param {{pixelSize:number, palette:Object<string,string>, rows:string[]}} def */
export function defineSprite({ pixelSize, palette, rows }) {
  return { pixelSize, palette, rows };
}

// Deterministic pseudo-random in [0,1) — sprites are built once at module
// load, but a plain Math.random() would still make two runs of the same
// boss look different, and re-importing (e.g. under Vitest) would too.
export function seededRandom(seed) {
  let state = seed >>> 0 || 1;
  return () => {
    state = (state * 1103515245 + 12345) & 0x7fffffff;
    return state / 0x7fffffff;
  };
}

/**
 * Builds a filled circular/oval blob of `rows` (all using palette key 'R'),
 * sized to roughly `radiusPx` across, optionally roughened with `spikes`
 * outward jags for a less-perfectly-round silhouette. Callers wrap the
 * result with their own palette (see data/sprites.js's buildBossMask).
 */
export function buildSilhouette(radiusPx, { spikes = 0, jitter = 0.15, seed = 1 } = {}) {
  const r = Math.max(2, Math.round(radiusPx));
  const size = r * 2 + 1;
  const rand = seededRandom(seed);
  // Precompute a per-angle spike multiplier so the jag pattern is smooth
  // (radially continuous) rather than per-pixel noise.
  const spikeAngles = [];
  for (let i = 0; i < spikes; i++) spikeAngles.push(rand() * Math.PI * 2);

  const rows = [];
  for (let y = 0; y < size; y++) {
    let row = '';
    for (let x = 0; x < size; x++) {
      const dx = x - r;
      const dy = y - r;
      const dist = Math.hypot(dx, dy);
      const angle = Math.atan2(dy, dx);
      let edge = r;
      for (const spikeAngle of spikeAngles) {
        const diff = Math.abs(Math.atan2(Math.sin(angle - spikeAngle), Math.cos(angle - spikeAngle)));
        if (diff < 0.35) edge = r * (1 + jitter * (1 - diff / 0.35));
      }
      row += dist <= edge ? 'R' : ' ';
    }
    rows.push(row);
  }
  return rows;
}

/**
 * Wraps `rows` in a 1px border of `outlineChar` along every silhouette edge
 * (any empty cell orthogonally touching a filled one) — pads the grid by 1
 * on every side first so the outline has room to sit outside the original
 * bounds. A dark outline is the single biggest "hand-authored pixel art"
 * cue a silhouette is missing without one; every sprite built purely from
 * buildSilhouette blobs reads as soft/basic until this is applied.
 */
export function outlineRows(rows, outlineChar) {
  const h = rows.length;
  const w = rows[0]?.length ?? 0;
  const grid = Array.from({ length: h + 2 }, () => Array(w + 2).fill(' '));
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) grid[y + 1][x + 1] = rows[y][x];
  }
  const isFilled = (y, x) => grid[y]?.[x] !== undefined && grid[y][x] !== ' ';
  const toOutline = [];
  for (let y = 0; y < grid.length; y++) {
    for (let x = 0; x < grid[y].length; x++) {
      if (grid[y][x] !== ' ') continue;
      if (isFilled(y - 1, x) || isFilled(y + 1, x) || isFilled(y, x - 1) || isFilled(y, x + 1)) {
        toOutline.push([y, x]);
      }
    }
  }
  for (const [y, x] of toOutline) grid[y][x] = outlineChar;
  return grid.map((r) => r.join(''));
}

/**
 * Upscales a hand-authored grid by `factor`, rounding its silhouette's
 * jagged staircase edges into a smoother slope instead of just enlarging the
 * same stair-steps (which is all plain per-cell repetition would do). Meant
 * for the boss sprites in data/sprites.js, which are placed by hand rather
 * than generated from a distance field like buildSilhouette, so there's no
 * formula to regenerate them at a different resolution — this reshapes the
 * existing art instead.
 *
 * Works in two passes:
 *  1. Treat "any non-space cell" as a binary fill mask and bilinearly
 *     resample it at the higher resolution, thresholding back to a crisp
 *     yes/no at 0.5. Bilinear resampling a hard step produces a ramp
 *     through the boundary; thresholding that ramp on a finer grid turns
 *     one big diagonal stair-step into several smaller ones — visually
 *     closer to a straight line. This only decides the *outer* silhouette
 *     shape, not which color goes where.
 *  2. For every cell the mask kept, pick which original character belongs
 *     there via nearest-neighbor onto the source grid, so interior color
 *     regions (an eye, a chest cavity, a crown accent) land in roughly the
 *     same place they did before, just rendered through more, smaller cells.
 *
 * No DOM/canvas dependency (deliberately) — this runs at module load in
 * data/sprites.js, which the plain-Node vitest environment also imports
 * transitively (via data/pets.js), and that environment has no `document`.
 */
export function smoothUpscale(rows, factor) {
  if (factor <= 1) return rows;
  const h = rows.length;
  const w = rows[0]?.length ?? 0;
  // Hand-authored rows aren't always padded to a uniform width (see the
  // BOSS_SPRITES literals in data/sprites.js), so a column can be in-bounds
  // for the grid's overall width `w` yet past the end of *this* row's own
  // (shorter) string — rows[y][x] returns undefined there, not ' '. Treat
  // that the same as background; leaving it as undefined would otherwise
  // get read as "not a space" (filled) below and, on the character-pick
  // path, get string-concatenated in literally as "undefined".
  const cellAt = (y, x) => {
    if (y < 0 || y >= h || x < 0 || x >= w) return ' ';
    const ch = rows[y][x];
    return ch === undefined ? ' ' : ch;
  };
  const maskAt = (y, x) => (cellAt(y, x) === ' ' ? 0 : 1);

  const outH = h * factor;
  const outW = w * factor;
  const out = [];
  for (let oy = 0; oy < outH; oy++) {
    let line = '';
    // Pixel-center mapping: the output cell's center lands exactly on the
    // source cell's center when factor === 1, and spreads evenly otherwise.
    const sy = (oy + 0.5) / factor - 0.5;
    const y0 = Math.floor(sy);
    const fy = sy - y0;
    for (let ox = 0; ox < outW; ox++) {
      const sx = (ox + 0.5) / factor - 0.5;
      const x0 = Math.floor(sx);
      const fx = sx - x0;

      const w00 = (1 - fx) * (1 - fy);
      const w10 = fx * (1 - fy);
      const w01 = (1 - fx) * fy;
      const w11 = fx * fy;
      const value =
        maskAt(y0, x0) * w00 + maskAt(y0, x0 + 1) * w10 +
        maskAt(y0 + 1, x0) * w01 + maskAt(y0 + 1, x0 + 1) * w11;

      if (value < 0.5) {
        line += ' ';
        continue;
      }
      // Nearest-neighbor character among the same 4 corners used above,
      // preferring whichever carried the most weight — falls through to the
      // next-heaviest corner if the nearest one happens to be background
      // (can happen right at a smoothed boundary).
      const corners = [
        { y: y0, x: x0, ch: cellAt(y0, x0), weight: w00 },
        { y: y0, x: x0 + 1, ch: cellAt(y0, x0 + 1), weight: w10 },
        { y: y0 + 1, x: x0, ch: cellAt(y0 + 1, x0), weight: w01 },
        { y: y0 + 1, x: x0 + 1, ch: cellAt(y0 + 1, x0 + 1), weight: w11 },
      ].sort((a, b) => b.weight - a.weight);
      const pick = corners.find((c) => c.ch !== ' ') ?? corners[0];
      line += pick.ch === ' ' ? 'R' : pick.ch;
    }
    out.push(line);
  }
  return out;
}

/**
 * Combines two partial paletteOverride maps for a single drawSprite call,
 * with `over` winning per key. Used so a persistent recolor (a cosmetic skin,
 * data/cosmetics.js) can be the base while a transient state flash
 * (hit/invincible/transform, Game._drawPlayer) still takes precedence on the
 * keys it touches. Returns null when neither is present (the cached,
 * override-free draw path). `over` alone with no base is the pre-cosmetic
 * behavior, unchanged.
 */
export function mergePaletteOverride(base, over) {
  if (!base && !over) return null;
  return { ...base, ...over };
}

/** Multiplies a #rrggbb color's channels by (1 - amount) — used to derive a shadow shade from a sprite's own body color instead of hand-picking one. */
export function darkenHex(hex, amount) {
  const n = parseInt(hex.slice(1), 16);
  const channel = (shift) => Math.round((((n >> shift) & 0xff) * (1 - amount)));
  return '#' + [channel(16), channel(8), channel(0)].map((v) => v.toString(16).padStart(2, '0')).join('');
}

/**
 * Perceptual lightness of a #rrggbb color, 0 (black) to 1 (white), on the
 * usual 0.299/0.587/0.114 weights. Used to decide whether text drawn *over* a
 * color should be white or near-black — see Game._arenaInk, which exists
 * because The Anthem's arena floor is the one light surface in the game.
 */
export function hexLuminance(hex) {
  const n = parseInt(hex.slice(1), 16);
  return (0.299 * ((n >> 16) & 0xff) + 0.587 * ((n >> 8) & 0xff) + 0.114 * (n & 0xff)) / 255;
}

// One fillRect per non-space grid cell. Shared by the cache-building pass
// (drawSprite's common case) and the rare paletteOverride pass (drawn live,
// uncached, since an override — boss phase tint, dash i-frame flash, weapon
// fire flash — is a transient substitution the shared sprite object doesn't
// own and isn't worth a cache entry for).
function drawSpriteCells(ctx, sprite, originX, originY, flipX, paletteOverride) {
  const { pixelSize, palette, rows } = sprite;
  const height = rows.length;
  const width = rows[0]?.length ?? 0;
  for (let row = 0; row < height; row++) {
    const line = rows[row];
    for (let col = 0; col < width; col++) {
      const key = line[col];
      if (key === ' ' || key === undefined) continue;
      const color = paletteOverride?.[key] ?? palette[key];
      if (!color) continue;
      const px = flipX ? width - 1 - col : col;
      ctx.fillStyle = color;
      ctx.fillRect(originX + px * pixelSize, originY + row * pixelSize, pixelSize, pixelSize);
    }
  }
}

// Renders a sprite once to its own offscreen canvas and stashes it on the
// (shared, singleton) sprite object — every later draw of that sprite in
// that orientation becomes a single drawImage instead of re-issuing one
// fillRect per grid cell every frame. That's what makes DETAIL/BOSS_DETAIL
// (data/sprites.js) affordable: a boss at BOSS_DETAIL=5 is ~7,500 cells,
// which cost a real, measured ~19ms/frame (60fps -> ~28fps) redrawn raw —
// bitmap-cached, it's back to a single blit regardless of grid size.
function getCachedBitmap(sprite, flipX) {
  const key = flipX ? '_bitmapFlipped' : '_bitmap';
  if (!sprite[key]) {
    const { pixelSize, rows } = sprite;
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.ceil((rows[0]?.length ?? 0) * pixelSize));
    canvas.height = Math.max(1, Math.ceil(rows.length * pixelSize));
    drawSpriteCells(canvas.getContext('2d'), sprite, 0, 0, flipX, null);
    sprite[key] = canvas;
  }
  return sprite[key];
}

/**
 * Draws `sprite.rows` centered on (x, y) in virtual-canvas space.
 * `paletteOverride` substitutes colors for specific palette keys just for
 * this call (boss phase tint, player dash i-frame flash-white, weapon-icon
 * fire flash) without mutating the shared sprite definition — those draws
 * skip the bitmap cache and fall back to the direct per-cell path.
 */
export function drawSprite(ctx, sprite, x, y, { flipX = false, paletteOverride = null } = {}) {
  if (!sprite) return;
  const { pixelSize, rows } = sprite;
  const height = rows.length;
  const width = rows[0]?.length ?? 0;
  const originX = x - (width * pixelSize) / 2;
  const originY = y - (height * pixelSize) / 2;

  if (paletteOverride) {
    drawSpriteCells(ctx, sprite, originX, originY, flipX, paletteOverride);
    return;
  }
  ctx.drawImage(getCachedBitmap(sprite, flipX), originX, originY);
}
